import React, { useEffect, useState } from "react";
import { Gauge, MagnifyingGlass, CaretDown, CaretUp } from "@phosphor-icons/react";
import { timingFdrPerf, timingFdrPerfDetail } from "../../lib/timing-api";
import { POSITIONS } from "../../lib/timing-i18n";
import { track } from "../../lib/analytics";

const SORTS = [
    ["hard", "PPG vs těžcí (4-5)"],
    ["easy", "PPG vs lehcí (1-2)"],
    ["diff", "Rozdíl L−T"],
    ["ppg", "PPG celkem"],
];

const FDR_COLORS = {
    1: "text-fa-mint", 2: "text-fa-mint", 3: "text-fa-yellow",
    4: "text-orange-300", 5: "text-red-300",
};

function SortTh({ k, sort, setSort, className = "", title, children }) {
    const active = sort === k;
    return (
        <th className={`py-2 text-right cursor-pointer select-none hover:text-white ${active ? "text-fa-sky" : ""} ${className}`}
            title={title || "Kliknutím seřadíš podle tohoto sloupce"}
            data-testid={`fdr-perf-col-${k}`}
            onClick={() => setSort(k)}>
            {children}{active ? " ▼" : ""}
        </th>
    );
}

function PpgCell({ b }) {
    if (!b.games) return <td className="px-2 py-2 text-right font-mono text-[10px] text-white/25">—</td>;
    return (
        <td className="px-2 py-2 text-right font-mono text-[11px] tabular-nums"
            title={`FDR ${b.fdr}: ${b.games} zápasů · haul ${b.haul_rate}% · blank ${b.blank_rate}%`}>
            <span className={`font-bold ${FDR_COLORS[b.fdr]}`}>{b.ppg?.toFixed(2)}</span>
            <span className="text-white/35 text-[8px] ml-1">({b.games})</span>
        </td>
    );
}

function DetailRow({ playerKey }) {
    const [detail, setDetail] = useState(null);
    useEffect(() => {
        track("fdr_perf_detail", { tool: "timing", meta: { player_key: playerKey } });
        timingFdrPerfDetail(playerKey).then(setDetail).catch(() => {});
    }, [playerKey]);
    if (!detail) return <div className="p-3 font-mono text-[10px] text-white/40">Načítám detail…</div>;
    return (
        <div className="p-3 space-y-3" data-testid={`fdr-perf-detail-${playerKey}`}>
            {detail.buckets.filter((b) => b.games > 0).map((b) => (
                <div key={b.fdr} className="border border-white/10 bg-black/30 p-2">
                    <div className="flex items-center gap-3 flex-wrap font-mono text-[10px] mb-1.5">
                        <span className={`uppercase tracking-widest font-bold ${FDR_COLORS[b.fdr]}`}>FDR {b.fdr}</span>
                        <span className="text-white/60 tabular-nums">{b.games} zápasů</span>
                        <span className="tabular-nums">PPG <strong className={FDR_COLORS[b.fdr]}>{b.ppg?.toFixed(2)}</strong></span>
                        <span className="text-fa-yellow tabular-nums">haul {b.haul_rate}%</span>
                        <span className="text-white/50 tabular-nums">blank {b.blank_rate}%</span>
                        <span className="text-white/50 tabular-nums">max {b.max_pts} b</span>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                        {b.opponents.map((o) => (
                            <span key={o.opponent}
                                  className="font-mono text-[9px] border border-white/15 px-1.5 py-0.5 whitespace-nowrap"
                                  title={`${o.games} zápasů · ${o.pts} bodů · ${o.hauls}× haul`}>
                                {o.opponent} <strong className="tabular-nums">{o.ppg.toFixed(1)}</strong>
                            </span>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function FdrPerfBoard() {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const [position, setPosition] = useState("");
    const [team, setTeam] = useState("");
    const [minGames, setMinGames] = useState(50);
    const [sort, setSort] = useState("hard");
    const [onlyActive, setOnlyActive] = useState(true);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => { track("fdr_perf_view", { tool: "timing" }); }, []);

    useEffect(() => {
        const tm = setTimeout(async () => {
            setBusy(true);
            try {
                setData(await timingFdrPerf({ q, position, team, minGames, sort, onlyActive }));
            } catch { /* ignore */ } finally { setBusy(false); }
        }, q ? 350 : 0);
        return () => clearTimeout(tm);
    }, [q, position, team, minGames, sort, onlyActive]);

    const rows = data?.rows || [];
    const activeFdrs = [1, 2, 3, 4, 5].filter((n) => rows.some((r) => r.buckets[n - 1]?.games > 0));

    return (
        <div className="space-y-4" data-testid="fdr-perf-board">
            <header className="border border-white/10 bg-[#0d0d0d] p-4">
                <h2 className="font-display font-black uppercase tracking-tight text-lg flex items-center gap-2">
                    <Gauge size={16} weight="fill" className="text-fa-sky" />
                    Výkon podle FDR — komu (ne)vadí těžcí soupeři
                </h2>
                <p className="font-mono text-[10px] text-white/50 mt-1 max-w-3xl leading-relaxed">
                    Historické zápasy 2016/17–2025/26 rozdělené podle obtížnosti soupeře (FDR dle aktuální síly týmu;
                    zaniklé PL kluby dopočítány). Klikni na hlavičku sloupce pro řazení, na řádek hráče pro detail.
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-1 mt-3 font-mono text-[10px] text-white/60 leading-relaxed" data-testid="fdr-perf-legend">
                    <span><strong className="text-white">PPG</strong> = průměr FPL bodů na zápas (min. 1 minuta na hřišti)</span>
                    <span><strong className="text-fa-sky">FDR 2–5</strong> = PPG proti soupeřům dané obtížnosti, v závorce počet zápasů</span>
                    <span><strong className="text-fa-yellow">haul %</strong> = podíl zápasů s 10+ body · <strong className="text-white/80">blank %</strong> = podíl zápasů s ≤2 body (v detailu)</span>
                    <span><strong className="text-white">Lehcí−Těžcí</strong> = PPG vs FDR 1-2 minus PPG vs FDR 4-5 · <span className="text-orange-300">záporné = daří se víc proti gigantům</span></span>
                </div>
            </header>

            <div className="flex items-center gap-2 flex-wrap" data-testid="fdr-perf-filters">
                <div className="relative">
                    <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-white/40" />
                    <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat hráče…"
                           data-testid="fdr-perf-search"
                           className="bg-[#0d0d0d] border border-white/15 pl-7 pr-2 py-1.5 font-mono text-[11px] w-48 focus:outline-none focus:border-fa-sky" />
                </div>
                <div className="flex items-center gap-1">
                    <button type="button" onClick={() => setPosition("")}
                            className={`font-mono text-[9px] uppercase px-2 py-1.5 border ${!position ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60"}`}>
                        Vše
                    </button>
                    {POSITIONS.map((p) => (
                        <button key={p} type="button" onClick={() => setPosition(position === p ? "" : p)}
                                data-testid={`fdr-perf-pos-${p}`}
                                className={`font-mono text-[9px] uppercase px-2 py-1.5 border ${position === p ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60"}`}>
                            {p}
                        </button>
                    ))}
                </div>
                <select value={team} onChange={(e) => setTeam(e.target.value)} data-testid="fdr-perf-team-filter"
                        className="bg-[#0d0d0d] border border-white/15 px-2 py-1.5 font-mono text-[10px] text-white/70 focus:outline-none max-w-[180px]">
                    <option value="">Všechny týmy</option>
                    {(data?.teams || []).map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <select value={minGames} onChange={(e) => setMinGames(Number(e.target.value))} data-testid="fdr-perf-min-games"
                        className="bg-[#0d0d0d] border border-white/15 px-2 py-1.5 font-mono text-[10px] text-white/70 focus:outline-none">
                    {[10, 30, 50, 100, 150].map((n) => <option key={n} value={n}>min {n} zápasů</option>)}
                </select>
                <label className="flex items-center gap-1.5 font-mono text-[10px] text-white/70 cursor-pointer border border-white/15 px-2 py-1.5"
                       data-testid="fdr-perf-only-active">
                    <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)}
                           className="accent-[#9EE8B4]" />
                    Jen aktivní hráči v PL
                </label>
                <div className="flex items-center gap-1 ml-auto">
                    {SORTS.map(([k, label]) => (
                        <button key={k} type="button" onClick={() => setSort(k)} data-testid={`fdr-perf-sort-${k}`}
                                className={`font-mono text-[9px] uppercase tracking-widest px-2 py-1.5 border ${sort === k ? "bg-fa-sky text-black border-fa-sky font-bold" : "border-white/15 text-white/60 hover:text-white"}`}>
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="border border-white/10 bg-[#0d0d0d] overflow-x-auto">
                <table className="w-full text-left" data-testid="fdr-perf-table">
                    <thead>
                        <tr className="border-b border-white/10 font-mono text-[9px] uppercase tracking-[0.2em] text-white/40">
                            <th className="px-3 py-2">#</th>
                            <th className="px-3 py-2">Hráč</th>
                            <SortTh k="games" sort={sort} setSort={setSort} className="px-3">Zápasy</SortTh>
                            <SortTh k="ppg" sort={sort} setSort={setSort} className="px-3" title="Průměr bodů na zápas celkem">PPG</SortTh>
                            {activeFdrs.map((n) => (
                                <SortTh key={n} k={`fdr${n}`} sort={sort} setSort={setSort}
                                        className={`px-2 ${FDR_COLORS[n]}`}
                                        title={`Řadit podle PPG proti FDR ${n} (min. 3 zápasy)`}>
                                    FDR {n}
                                </SortTh>
                            ))}
                            <SortTh k="diff" sort={sort} setSort={setSort} className="px-3"
                                    title="PPG vs FDR 1-2 minus PPG vs FDR 4-5 · záporné = giant killer">
                                Lehcí−Těžcí
                            </SortTh>
                            <th className="px-3 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <React.Fragment key={r.player_key}>
                                <tr className="border-b border-white/5 hover:bg-white/[0.03] cursor-pointer"
                                    data-testid={`fdr-perf-row-${r.player_key}`}
                                    onClick={() => setExpanded(expanded === r.player_key ? null : r.player_key)}>
                                    <td className="px-3 py-2 font-mono text-[10px] text-white/40 tabular-nums">{i + 1}</td>
                                    <td className="px-3 py-2">
                                        <span className="font-display font-bold text-sm">{r.display_name}</span>
                                        <span className="font-mono text-[8px] uppercase text-white/40 ml-2">
                                            {(r.positions || []).join("/")}{r.team ? ` · ${r.team}` : ""} · {r.seasons_count}× sez.
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-[10px] text-white/60 tabular-nums">{r.games}</td>
                                    <td className="px-3 py-2 text-right font-mono text-[11px] font-bold tabular-nums">{r.ppg?.toFixed(2)}</td>
                                    {activeFdrs.map((n) => <PpgCell key={n} b={r.buckets[n - 1]} />)}
                                    <td className={`px-3 py-2 text-right font-mono text-[11px] tabular-nums ${r.diff > 1 ? "text-fa-mint" : r.diff < 0 ? "text-orange-300" : "text-white/70"}`}
                                        title={r.diff < 0 ? "Proti těžkým soupeřům bodude víc než proti lehkým — giant killer" : "Kladné = daří se víc proti slabším"}>
                                        {r.diff > 0 ? "+" : ""}{r.diff?.toFixed(2)}
                                    </td>
                                    <td className="px-3 py-2 text-fa-sky">
                                        {expanded === r.player_key ? <CaretUp size={12} /> : <CaretDown size={12} />}
                                    </td>
                                </tr>
                                {expanded === r.player_key && (
                                    <tr className="border-b border-white/5 bg-black/40">
                                        <td colSpan={11}><DetailRow playerKey={r.player_key} /></td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
                {busy && <div className="p-4 font-mono text-[10px] text-white/40">Načítám…</div>}
                {!busy && rows.length === 0 && (
                    <div className="p-6 font-mono text-[11px] text-white/50 text-center" data-testid="fdr-perf-empty">
                        Žádní hráči neodpovídají filtrům.
                    </div>
                )}
            </div>
            {data && (
                <p className="font-mono text-[9px] text-white/35">
                    Zobrazeno {rows.length} z {data.total_matching} hráčů (min. {minGames} zápasů).
                </p>
            )}
        </div>
    );
}
